import axios, { AxiosInstance } from 'axios';

import apiUrls from '@/constants/apiUrls';
import { API_URI, PROTOCOL_CHECK, PROTOCOL_HOST_CHECK } from '@/constants/env';
import { normalizeHost } from '@/helpers/utilities';
import { ActivityPayoutDetail, ActivityRedemptionDetail } from '@/models/activityModels';
import type { AutoTranslateLanguage } from '@/models/customizationModels';
import { SaveShareLinkType, ShareLink } from '@/models/shareModels';

import { AccountNotifications, SaveAmbassadorType, SaveNotificationsResponse, SaveNotificationsType } from '../models/accountModels';
import {
  AccountActivation,
  AccountActivationResponse,
  ActivitiesResponse,
  AmbassadorProfile,
  AuthUser,
  ChangePasswordResponse,
  RegisterFormData,
  RegisterResponse,
  ResetPasswordResponse,
  SaveAmbassadorResponse,
  StoreFrontValidationResponse,
  UserAccount,
} from '../models/authModels';
import { CampaignStats, TrackClickType } from '../models/campaignModels';
import { CompanyAccount } from '../models/companyModels';
import { CustomerAdvocacyBadgeItemType } from '../models/customerAdvocacyModels';
import { Paginator } from '../models/globalModels';
import { BadgeItemType, LoyaltyTask, LoyaltyTier } from '../models/loyaltyModels';
import { Redemption, Reward, VaultItemBalance, VaultItemReward } from '../models/rewardsModel';
import { StatsMetric } from '../models/statsModels';
import { TypeHeadOptionConfig } from '../models/tableModels';
import { TaxContext } from '../models/taxModels';

import { setAuthToken } from './api/axiosInstance';
import ErrorHandler, { ErrorCause, ErrorSource } from './ErrorHandler';
import LocalDataService from './LocalDataService';

export type globalType = {
  apiInstance?: ApiService;
};

export const envVars = {
  ambassadorApiUri: API_URI,
  protocol: PROTOCOL_CHECK,
  checkHost: PROTOCOL_HOST_CHECK,
};

class ApiService {
  axios: AxiosInstance;
  token?: string;

  constructor() {
    if ((global as globalType).apiInstance) {
      throw new Error('A new service instance could not be created.');
    } else {
      this.axios = axios.create({
        baseURL: envVars.ambassadorApiUri,
        timeout: 20000,
        headers: {
          'Content-Type': 'application/json',
        },
      });
    }
    (global as globalType).apiInstance = this;
  }

  getInstance() {
    return this;
  }

  setToken(token: string) {
    this.axios.defaults.headers.common['Authorization'] = `Token ${token}`;
    this.token = token;
    setAuthToken(token); // TODO: Temporal Authorization
  }

  setLanguage(lang: string) {
    this.axios.defaults.headers.common['Language'] = lang;
  }

  buildError(e: any) {
    if (typeof e == 'string') return e;
    if (!e.response || !e.response.data) return e.message;
    return Object.entries(e.response.data).reduce(
      (accumulator, currentValue) => `${accumulator}${accumulator == '' ? '' : ','} ${currentValue[0]}: ${currentValue[1]}`,
      '',
    );
  }

  /* AUTH CALLS */
  doLogin(email: string, password: string, host: string) {
    return new Promise<AuthUser>((resolve, reject) => {
      const body = { email, password };
      this.axios
        .post(`${apiUrls.login}?host=${host}`, body, {
          headers: {
            // dont send token in login endpoint
            Authorization: undefined,
          },
        })
        .then((response) => {
          const user = response.data as AuthUser;
          resolve(user);
        })
        .catch((e) => {
          console.log(e);
          const error = ErrorHandler.get(e, ErrorSource.LOGIN_AUTH, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  doRegister(formData: RegisterFormData, headers: object, companyId: number) {
    return new Promise<RegisterResponse>((resolve, reject) => {
      const body = formData;
      const options: object = { headers };
      this.axios
        .post(`${apiUrls.companies}${companyId}${apiUrls.register}`, body, options)
        .then((response) => {
          const createdUser = response.data as RegisterResponse;
          resolve(createdUser);
        })
        .catch((e) => {
          console.log(e);
          const error = ErrorHandler.get(e, ErrorSource.USER_REGISTER, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  // creates a session on the API using pusher to get IDs
  createSession() {
    return new Promise<any>((resolve, reject) => {
      this.axios
        .post(`${apiUrls.session}`, [])
        .then((response) => {
          const session = response.data;
          resolve(session);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.USER_SESSION, ErrorCause.API_CALL, e.response?.data);
          reject(error);
        });
    });
  }

  async getCompanyByHost(host: string) {
    const h = normalizeHost(host);
    const portalUrl = `/portal.json?host=${encodeURIComponent(h!)}`;
    const listUrl = `${apiUrls.companyByHost}${h}`;

    const pickCompany = (data: any): CompanyAccount | null => {
      if (!data) return null; // handles null / undefined
      if (typeof data === 'object' && data.uid) return data as CompanyAccount; // portal.json object
      if (Array.isArray(data) && data[0]?.uid) return data[0] as CompanyAccount; // legacy array
      if (typeof data === 'object' && data.results?.[0]?.uid) return data.results[0] as CompanyAccount; // fallback results array
      return null;
    };

    // 1) portal.json endpoint
    try {
      const r = await this.axios.get(portalUrl, { headers: { Authorization: undefined } });
      const company = pickCompany(r.data);
      if (company) return company;
    } catch (e: any) {
      // ignore and fall back unless this is a hard 5xx
      if (e?.response?.status && e.response.status >= 500) throw e;
    }

    // 2) company by host endpoint
    try {
      const r = await this.axios.get(listUrl, { headers: { Authorization: undefined } });
      const company = pickCompany(r.data);
      if (company) return company;
    } catch (e) {
      // fall through to throw below
    }

    throw new Error(`[getCompanyByHost] Could not resolve company from host "${h}".`);
  }

  getUser(userId: number) {
    return new Promise<UserAccount>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.user}${userId}/`)
        .then((response) => {
          const user = response.data as UserAccount;
          resolve(user);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.USER_GET_ACCT, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  getAmbassadorFromCompany(companyUid: number) {
    return new Promise<UserAccount>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.companies}${companyUid}/ambassadors/`)
        .then((response) => {
          const user = response.data;
          if (user.results && user.results.length == 1) {
            const ambassador = user.results[0] as UserAccount;
            resolve(ambassador);
          } else {
            reject('[Error] Expected result array containing ambassador user.');
          }
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.LOGIN_AMBASSADOR_PROFILE, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  getAmbassadorProfile(uid: number) {
    return new Promise<AmbassadorProfile>((resolve, reject) => {
      this.axios
        .get(`/portal${apiUrls.ambassadorProfile}${uid}/`)
        .then((response) => {
          const ambassador = response.data as AmbassadorProfile;
          resolve(ambassador);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.LOGIN_AMBASSADOR_PROFILE, ErrorCause.API_CALL, e?.response?.data);
          reject(error);
        });
    });
  }

  getAccountNotifications(uid: number) {
    return new Promise<AccountNotifications>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.ambassadorProfile}${uid}/notifications/`)
        .then((response) => {
          const notifications = response.data as AccountNotifications;
          resolve(notifications);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.ACCT_GET_NOTIF, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  changePassword(userid: number, body: object) {
    return new Promise<ChangePasswordResponse>((resolve, reject) => {
      this.axios
        .post(`${apiUrls.user}${userid}${apiUrls.changePassword}`, body, { timeout: 15000 })
        .then((response) => {
          const res = response.data as ChangePasswordResponse;
          resolve(res);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.ACCT_CHANGE_PASSWORD, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  saveAmbassador(uid: number, body: SaveAmbassadorType, multipart = false) {
    return new Promise<SaveAmbassadorResponse>((resolve, reject) => {
      this.axios
        .patch(`${apiUrls.ambassadorPortalProfile}${uid}/`, body, {
          timeout: 15000,
          headers: multipart ? { 'Content-Type': 'multipart/form-data' } : {},
        })
        .then((response) => {
          const res = response.data as SaveAmbassadorResponse;
          resolve(res);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.ACCT_SAVE_AMBASSADOR, ErrorCause.API_CALL, e?.response?.data);
          reject(error);
        });
    });
  }

  saveAcctNotifications(uid: number, body: SaveNotificationsType) {
    return new Promise<SaveNotificationsResponse>((resolve, reject) => {
      this.axios
        .patch(`${apiUrls.ambassadorProfile}${uid}/notifications/`, body, { timeout: 15000 })
        .then((response) => {
          const res = response.data as SaveNotificationsResponse;
          resolve(res);
        })
        .catch((e) => {
          console.log(e);
          const error = ErrorHandler.get(e, ErrorSource.ACCT_SAVE_NOTIFICATIONS, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  saveAcctActivationStatus(uid: number, body: AccountActivation) {
    return new Promise<AccountActivationResponse>((resolve, reject) => {
      this.axios
        .patch(`${apiUrls.ambassadorProfile}${uid}${apiUrls.accountActivation}`, body, { timeout: 15000 })
        .then((response) => {
          const res = response.data as AccountActivationResponse;
          resolve(res);
        })
        .catch((e) => {
          console.log(e);
          const error = ErrorHandler.get(e, ErrorSource.ACCT_ACTIVATION_STATUS, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  /* LOYALTY */
  getLoyaltyBadges(companyId: number) {
    return new Promise<Paginator<BadgeItemType>>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.companies}${companyId}${apiUrls.loyaltyBadges}`)
        .then((response) => {
          const badges = response.data;
          resolve(badges);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.LOYALTY_GET_BADGES, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  getLoyaltyTiers(companyId: number) {
    return new Promise<Paginator<LoyaltyTier>>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.companies}${companyId}${apiUrls.loyaltyTiers}`)
        .then((response) => {
          const tiers = response.data;
          resolve(tiers);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.LOYALTY_GET_TIERS, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  getLoyaltyTasks(companyId: number) {
    return new Promise<Paginator<LoyaltyTask>>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.companies}${companyId}${apiUrls.loyaltyTasks}`)
        .then((response) => {
          const tasks = response.data;
          resolve(tasks);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.LOYALTY_GET_TASKS, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  /* CUSTOMER ADVOCACY */
  getCustomerAdvocacyBadges(companyId: number) {
    return new Promise<Paginator<CustomerAdvocacyBadgeItemType>>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.companies}${companyId}${apiUrls.customerAdvocacyBadges}`)
        .then((response) => {
          const badges = response.data;
          resolve(badges);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.CUSTOMER_ADVOCACY_GET_BADGES, ErrorCause.API_CALL, e.response.data);
          reject(error);
        });
    });
  }

  getRewards(uid?: number, page = 1) {
    return new Promise<Paginator<Reward>>((resolve, reject) => {
      this.axios
        .post(`/tables/ambassadors/${uid}/content/affiliate_gift_card/?page=${page}`)
        .then((response) => {
          const activites = response.data as Paginator<Reward>;
          resolve(activites);
        })
        .catch((e) => reject(this.buildError(e)));
    });
  }

  getTaxContext(uid: number) {
    return new Promise<TaxContext>((resolve, reject) => {
      this.axios
        .get(apiUrls.ambassadorPortalTaxContext(uid))
        .then((response) => {
          resolve(response.data as TaxContext);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.W9_TAX_CONTEXT, ErrorCause.API_CALL, e?.response?.data);
          reject(error);
        });
    });
  }

  async getVaultItemRewards(affiliate_uid: number, campaign_uid?: number, page?: number): Promise<Paginator<VaultItemReward>> {
    const params = new URLSearchParams();
    if (campaign_uid) params.append('campaign_id', String(campaign_uid));
    if (page && page > 1) params.append('page', String(page));
    const url = `${apiUrls.getVaultItemRewards(affiliate_uid)}?${params.toString()}`;
    const response = await fetch(url, {
      headers: { Authorization: `Token ${this.token}` },
    });
    if (!response.ok) throw this.buildError(new Error(`getVaultItemRewards failed: ${response.status}`));
    return response.json() as Promise<Paginator<VaultItemReward>>;
  }

  async getVaultItemBalance(affiliate_uid: number, id: number): Promise<VaultItemBalance> {
    const response = await fetch(apiUrls.getVaultItemBalance(affiliate_uid, id), {
      headers: { Authorization: `Token ${this.token}` },
    });
    if (!response.ok) throw this.buildError(new Error(`getVaultItemBalance failed: ${response.status}`));
    return response.json() as Promise<VaultItemBalance>;
  }

  async redeemReward(affiliate_uid: number, reward: Reward): Promise<any> {
    const body = { affiliate_uid, gift_card_uid: reward.uid, amount: reward.points };
    const response = await fetch(apiUrls.redeemRewardProxy(affiliate_uid), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${this.token}`,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15000),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw ErrorHandler.get(
        Object.assign(new Error('redeemReward failed'), { response: { data } }) as any,
        ErrorSource.REDEEM_REWARD,
        ErrorCause.API_CALL,
        { data, request: { status: response.status } },
      );
    }
    return data?.results;
  }

  async redeemVaultItemReward(affiliate_uid: number, id: number, variant_id: number, amount: number): Promise<any> {
    const body = { variant_item: variant_id, amount };
    const response = await fetch(apiUrls.redeemVaultItemReward(affiliate_uid, id), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${this.token}`,
      },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15000),
    });
    const xRequestId = response.headers.get('x-request-id');
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = ErrorHandler.get(
        Object.assign(new Error('redeemVaultItemReward failed'), { response: { data, status: response.status } }) as any,
        ErrorSource.REDEEM_REWARD,
        ErrorCause.API_CALL,
        data,
      ) as any;
      err.xRequestId = xRequestId;
      throw err;
    }
    return data.results;
  }

  async getRedemptionDetail(affiliate_uid: number, redemptionId: number): Promise<Redemption> {
    const response = await fetch(apiUrls.getRedemptionDetail(affiliate_uid, redemptionId), {
      headers: { Authorization: `Token ${this.token}` },
    });
    if (!response.ok) throw this.buildError(new Error(`getRedemptionDetail failed: ${response.status}`));
    return response.json() as Promise<Redemption>;
  }

  getOptionsForTypeAhead(options: TypeHeadOptionConfig, query?: string) {
    return this.axios
      .get<Paginator<any>>(options.url + query)
      .then((response) => {
        return response.data.results;
      })
      .catch((e) => this.buildError(e));
  }

  getPayoutDetails(id: number) {
    return this.axios.get<ActivityPayoutDetail>(apiUrls.payoutDetails(id));
  }

  getRedemptionDetails(id: number) {
    return this.axios.get<ActivityRedemptionDetail>(apiUrls.redemptionDetails(id));
  }

  forgotPassword(email: string) {
    return new Promise<any>((resolve, reject) => {
      const body = { email };
      this.axios
        .post(apiUrls.forgotPassword + window.location.search, body, { timeout: 15000 })
        .then((response: any) => {
          const responseObj = response.data as any;
          resolve(responseObj);
        })
        .catch((e) => reject(e));
    });
  }

  getActivities(affiliate_uid: number, uid: number) {
    return new Promise<ActivitiesResponse>((resolve, reject) => {
      this.axios
        .get(`${apiUrls.recentActivites}?affiliate_uid=${affiliate_uid}&campaign_uid=${uid}&page_size=15`)
        .then((response) => {
          const activites = response.data as ActivitiesResponse;
          resolve(activites);
        })
        .catch((e) => reject(this.buildError(e)));
    });
  }

  getAmbassadorStatsByCampaign(campaign_uid: number, uid: number, start_date?: string, end_date?: string) {
    return new Promise<CampaignStats>((resolve, reject) => {
      let url = `${apiUrls.ambassadorProfile}${uid}${apiUrls.campaignStats}?campaign_uid=${campaign_uid}`;
      if (start_date && end_date) {
        url += `&start_date=${start_date}&end_date=${end_date}`;
      }
      this.axios
        .get(url)
        .then((response) => {
          const data = response.data;
          if (data?.campaign_links && data?.campaign_links.length > 0) {
            const stats = data.campaign_links[0] as CampaignStats;
            resolve(stats);
          } else {
            reject(this.buildError({ message: 'No stats found' }));
          }
        })
        .catch((e) => reject(this.buildError(e)));
    });
  }
  getAmbassadorStats(uid: number, campaign_uid?: number) {
    return new Promise<CampaignStats>((resolve, reject) => {
      const baseUrl = `${apiUrls.ambassadorProfile}${uid}/stats/`;
      const params = new URLSearchParams({ split_currency_types: 'true' });

      if (campaign_uid) {
        params.append('campaign_uid', String(campaign_uid));
      }

      this.axios
        .get(`${baseUrl}?${params.toString()}`)
        .then((response) => {
          const data = response.data;
          if (data) {
            const stats = data as CampaignStats;
            stats.monetary_pending_sum = stats.commissions?.monetary?.pending_sum || 0;
            stats.non_monetary_pending_sum = stats.commissions?.non_monetary?.pending_sum || 0;
            resolve(stats);
          } else {
            reject(this.buildError({ message: 'No stats found' }));
          }
        })
        .catch((e) => reject(this.buildError(e)));
    });
  }

  resetPassword(email: string, password: string, repeat_password: string, code: string, host: string) {
    return new Promise<ResetPasswordResponse>((resolve, reject) => {
      const body = { code, email, password, repeat_password };
      this.axios
        .post(`${apiUrls.resetPassword}?host=${host}`, body, { timeout: 15000 })
        .then((response) => {
          const responseObj = response.data as ResetPasswordResponse;
          resolve(responseObj);
        })
        .catch((e) => {
          const error = ErrorHandler.get(e, ErrorSource.RESET_PASSWORD, ErrorCause.API_CALL, e.response?.data);
          reject(error);
        });
    });
  }

  getStatsMetrics(
    campaign_uid: number | undefined,
    uid: number,
    start_date: string,
    end_date: string,
    interval: string,
    stat_types?: string,
  ) {
    return new Promise<StatsMetric[]>((resolve, reject) => {
      let params = new URLSearchParams({
        end_date: end_date,
        start_date: start_date,
        interval: interval,
        stat_types: stat_types || 'clicks,shares,referrals',
      });

      if (campaign_uid) {
        params.append('campaign_uid', String(campaign_uid));
      }

      this.axios
        .get(`${apiUrls.ambassadorProfile}${uid}${apiUrls.statsGraphMetrics}?${params.toString()}`)
        .then((response) => {
          if (response && response?.data?.length > 0) {
            const metrics = response?.data as StatsMetric[];
            resolve(metrics);
          } else {
            reject(this.buildError({ message: 'No stats metrics found' }));
          }
        })
        .catch((e) => reject(this.buildError(e)));
    });
  }

  postLoyaltyData(userId: number, loyalty_data: object) {
    return new Promise<any>((resolve, reject) => {
      const body = { loyalty_data };
      this.axios
        .post(`${apiUrls.ambassadorProfile}${userId}/loyalty/data/`, body, { timeout: 15000 })
        .then((response) => {
          const responseObj = response.data as any;
          resolve(responseObj);
        })
        .catch((e) => {
          reject(this.buildError(e));
        });
    });
  }

  createShareLink(shareLink: Omit<SaveShareLinkType, 'uid'>) {
    const ambassador = LocalDataService.getUid();
    return this.axios
      .post<ShareLink>(apiUrls.shareCreate(ambassador), shareLink, { timeout: 15000 })
      .then((response) => {
        return response.data;
      })
      .catch((e) => {
        throw ErrorHandler.get(e, ErrorSource.SAVE_SHARELINK, ErrorCause.API_CALL, e?.response?.data);
      });
  }

  saveShareLink(shareLink: SaveShareLinkType) {
    return this.axios
      .patch<ShareLink>(`${apiUrls.urls}${shareLink.uid}/`, shareLink, { timeout: 15000 })
      .then((response) => {
        return response.data;
      })
      .catch((e) => {
        throw ErrorHandler.get(e, ErrorSource.SAVE_SHARELINK, ErrorCause.API_CALL, e?.response?.data);
      });
  }

  trackShareClick(data: TrackClickType) {
    return new Promise<any>((resolve, reject) => {
      const body = data;
      this.axios
        .post(`${apiUrls.trackShareClick}`, body, { timeout: 15000 })
        .then((response) => {
          const responseObj = response.data as any;
          resolve(responseObj);
        })
        .catch((e) => {
          reject(this.buildError(e));
        });
    });
  }

  saveUnsubscribeSettings(code: string, action: string, host: string, email_template?: string) {
    return new Promise<any>((resolve, reject) => {
      const body = { code, action, email_template };
      this.axios
        .post(`${apiUrls.unsubscribe}?host='${host}'`, body)
        .then((response) => {
          const responseObj = response.data as any;
          resolve(responseObj);
        })
        .catch((e) => {
          reject(this.buildError(e));
        });
    });
  }

  async getLanguages(companyId: number) {
    const response = await this.axios.get<Paginator<AutoTranslateLanguage>>(
      `/companies/${companyId}/languages/?ordering=language_uid__name&page_size=200`,
    );

    return response.data.results;
  }

  /* STOREFRONT AUTH — routed through Next.js API proxy to avoid CORS */
  async doStorefrontValidation(storefront_code: string): Promise<StoreFrontValidationResponse> {
    let response: Response;
    try {
      response = await fetch(apiUrls.storefrontValidation, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storefront_code }),
        signal: AbortSignal.timeout(15000),
      });
    } catch (e: any) {
      const err = new Error(e?.message || 'storefront validation request failed') as any;
      err.xRequestId = null;
      throw err;
    }

    const xRequestId = response.headers.get('x-request-id');
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const err = new Error('storefront validation failed') as any;
      err.xRequestId = xRequestId;
      err.response = { data, status: response.status };
      throw err;
    }

    return data as StoreFrontValidationResponse;
  }

  async doStorefrontLogin(payload: { storefront_code: string } & Record<string, string>): Promise<AuthUser> {
    let response: Response;
    try {
      response = await fetch(apiUrls.storefrontLogin, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: AbortSignal.timeout(15000),
      });
    } catch (e: any) {
      const err = new Error(e?.message || 'storefront login request failed') as any;
      err.xRequestId = null;
      throw err;
    }

    const xRequestId = response.headers.get('x-request-id');
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const err = new Error('storefront login failed') as any;
      err.xRequestId = xRequestId;
      err.response = { data, status: response.status };
      throw err;
    }

    return data as AuthUser;
  }

  async getCompanyByUid(uid: number) {
    if (!uid || Number.isNaN(uid)) {
      throw new Error(`[getCompanyByUid] Invalid uid "${uid}"`);
    }

    const pickCompany = (data: any): CompanyAccount | null => {
      if (!data) return null;
      // handles proper portal.json object
      if (typeof data === 'object' && data.uid) return data as CompanyAccount;
      if (Array.isArray(data) && data[0]?.uid) return data[0] as CompanyAccount;
      if (typeof data === 'object' && data.results?.[0]?.uid) return data.results[0] as CompanyAccount;
      return null;
    };

    const r = await this.axios.get(apiUrls.companyByUid + encodeURIComponent(String(uid)), { headers: {} });
    const company = pickCompany(r.data);
    if (company) return company;

    throw new Error(`[getCompanyByUid] Could not resolve company from uid "${uid}".`);
  }

  async getCompanyByRef(ref: { host?: string; uid?: number }) {
    if (ref?.uid) return this.getCompanyByUid(ref.uid);
    if (ref?.host) return this.getCompanyByHost(ref.host);
    throw new Error('[getCompanyByRef] Missing host or uid');
  }
}

let ApiServiceSingleton;
if (!(global as globalType).apiInstance) ApiServiceSingleton = new ApiService();
else ApiServiceSingleton = (global as globalType).apiInstance;
export default ApiServiceSingleton as ApiService;
